import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  AssetKindSchema,
  type BriefSource,
  type Note,
  type Project,
  type Rendition,
  type Segment,
  type Space,
} from "@scenix/core";
import { ApiError, api, type DraftSegment } from "../lib/api";
import { allBut } from "../lib/enums";
import {
  anyRendering,
  applyCut,
  applyTrim,
  assemblyOf,
  notesIn,
  pictureOf,
  plannedSegment,
  progress,
  saysNothing,
  segmentsIn,
  timecode,
  toDraft,
  withSource,
  withWords,
  type Span,
} from "../lib/scrub";
import { ChainPlayer } from "./ChainPlayer";
import { RefusalLine, type Refusal } from "./RefusalLine";
import { ScrubBar } from "./ScrubBar";

/**
 * The kinds of asset that can stand in as a segment's picture: every kind but
 * the two that are only text, which have nothing to show for their duration.
 */
const FOOTAGE_KINDS = new Set(allBut(AssetKindSchema.options, ["document", "link"]));

/**
 * Where a refused write belongs on this panel (ADR 000130).
 *
 * `panel` is the one place that is always rendered, which is why a conflict
 * goes there: handling one clears the selection, taking the range menu and
 * most of the controls that can earn a conflict with it.
 */
type Place = "panel" | "chain" | "range" | "brief" | "footage" | "comment" | `note:${Note["id"]}`;

interface TimelinePanelProps {
  spaceId: Space["id"];
  projectId: Project["id"];
  /**
   * Whether the caller may write to this project. Every mutation this panel
   * fires other than leaving a note is Editor-only on the API (ADR 000036);
   * a viewer who can select a range and click Trim just gets a 403, so the
   * actions themselves are hidden rather than left to fail.
   */
  mayEdit: boolean;
}

/**
 * The timeline, its notes, and what you can do to a selected range.
 *
 * The loop this exists to serve: watch, select the part that is wrong, say what
 * is wrong with it, resubmit. Trim and cut apply immediately because they are
 * exact; a comment is left for the agent to interpret (ADR 000023).
 */
export function TimelinePanel({ spaceId, projectId, mayEdit }: TimelinePanelProps) {
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState<Span | null>(null);
  // What the pointer is over, which is a different question from what is
  // selected: hovering looks, selecting acts (ADR 000064).
  const [hovered, setHovered] = useState<Segment | null>(null);
  const [playheadSec, setPlayheadSec] = useState<number | null>(0);
  const [comment, setComment] = useState("");
  const [failure, setFailure] = useState<Refusal<Place> | null>(null);

  // The footage picker: which asset, and how much of it. Kept apart from
  // `comment`/`selection` because it has its own multi-step flow (pick an
  // asset, then narrow the range) that Cancel and a successful attach both
  // need to reset together.
  const [footagePicker, setFootagePicker] = useState(false);
  const [pickedAssetId, setPickedAssetId] = useState("");
  const [rangeStart, setRangeStart] = useState(0);
  const [rangeEnd, setRangeEnd] = useState(0);

  const closeFootagePicker = () => {
    setFootagePicker(false);
    setPickedAssetId("");
    setRangeStart(0);
    setRangeEnd(0);
  };

  // The brief in words: what the segment is, and what is said over it. Held as
  // a draft rather than written straight through, because a brief is prose
  // somebody is composing and a keystroke is not an edit (ADR 000026).
  //
  // The segment it was opened on is remembered rather than a bare "open" flag,
  // so selecting a different one puts the editor away instead of showing the
  // last segment's words over the new one's.
  const [editingId, setEditingId] = useState<Segment["id"] | null>(null);
  const [draftLabel, setDraftLabel] = useState("");
  const [draftDirection, setDraftDirection] = useState("");
  const [draftNarration, setDraftNarration] = useState("");

  const openBriefEditor = (segment: Segment) => {
    setEditingId(segment.id);
    setDraftLabel(segment.label);
    setDraftDirection(segment.brief.direction);
    setDraftNarration(segment.brief.narration);
  };

  /**
   * Moves the selection, which is what every action on this page hangs off.
   *
   * Words being composed against the old range go with it. A brief half written
   * for one segment reappearing over the next one somebody selects would be an
   * edit nobody asked for, waiting behind a Save button.
   */
  const selectRange = (span: Span | null) => {
    setSelection(span);
    setEditingId(null);
  };

  const timelineKey = ["timeline", spaceId, projectId];
  const notesKey = ["notes", spaceId, projectId];

  // Loaded only once the picker is open: browsing the timeline should not cost
  // a corpus fetch nobody asked for.
  const assets = useQuery({
    queryKey: ["assets", spaceId, ""],
    queryFn: () => api.listAssets(spaceId),
    enabled: footagePicker,
  });
  const footage = (assets.data ?? []).filter(
    (asset) => asset.status === "ready" && FOOTAGE_KINDS.has(asset.kind),
  );
  const pickedAsset = footage.find((asset) => asset.id === pickedAssetId);

  /**
   * A conflict is not an ordinary failure. Somebody else changed the chain
   * first, so the fix is to re-read rather than to show an error and stop
   * (ADR 000027).
   */
  function handleFailure(where: Place, error: unknown) {
    if (error instanceof ApiError && error.isConflict) {
      void queryClient.invalidateQueries({ queryKey: timelineKey });
      selectRange(null);
      // Said at the panel rather than at `where`: clearing the selection
      // unmounts the range menu, so the line beside the button would go with
      // the button (ADR 000130).
      setFailure({
        where: "panel",
        message:
          "Somebody else changed this timeline while you were editing. Reloaded it; try again.",
      });
      return;
    }
    setFailure({ where, message: describe(error) });
  }

  const timeline = useQuery({
    queryKey: timelineKey,
    queryFn: () => api.getTimeline(spaceId, projectId),
    // Rendering happens in a worker (ADR 000056), so a segment goes queued,
    // rendering, ready with nothing on this page doing the work. Without this,
    // asking for a render and waiting shows queued until somebody reloads.
    refetchInterval: (query) => (anyRendering(query.state.data?.renditions ?? []) ? 4000 : false),
  });

  const notes = useQuery({
    queryKey: notesKey,
    queryFn: () => api.listNotes(spaceId, projectId),
  });

  // `where` is the only argument here that is not part of the write: this one
  // mutation is fired from the range actions and from the footage picker, and
  // the refusal has to land beside whichever was pressed (ADR 000130).
  const replaceSegments = useMutation({
    mutationFn: ({
      segments: next,
      expectedVersion,
    }: {
      segments: readonly DraftSegment[];
      expectedVersion: number;
      where: Place;
    }) => api.replaceSegments(spaceId, projectId, next, expectedVersion),
    onSuccess: (updated) => {
      queryClient.setQueryData(timelineKey, updated);
      selectRange(null);
      setFailure(null);
      closeFootagePicker();
    },
    onError: (error, { where }) => handleFailure(where, error),
  });

  /**
   * A mechanical edit: record what was asked for, then do it.
   *
   * Sequential, not concurrent. The note anchors to the version the range
   * refers to, and replacing the segments bumps that version, so firing both at
   * once would anchor the note to whichever request the server happened to
   * handle first.
   *
   * The note is marked addressed straight away because the browser applied it
   * exactly. Whether it worked is still a person's call, which is the line
   * between addressed and accepted (ADR 000023).
   */
  const applyEdit = useMutation({
    mutationFn: async ({
      kind,
      range,
      segments: next,
      expectedVersion,
    }: {
      kind: "trim" | "cut";
      range: Span;
      segments: readonly DraftSegment[];
      expectedVersion: number;
    }) => {
      const note = await api.createNote(spaceId, projectId, { track: 0, range, kind });
      let updated;
      try {
        updated = await api.replaceSegments(spaceId, projectId, next, expectedVersion);
      } catch (error) {
        // The note above already exists. Without this it sits open forever,
        // describing an edit that never actually happened.
        await api
          .updateNote(spaceId, projectId, note.id, {
            status: "dismissed",
            resolutionNote: "Not applied: the write that would have made this real was refused.",
          })
          .catch(() => {});
        throw error;
      }
      await api.updateNote(spaceId, projectId, note.id, {
        status: "addressed",
        resolutionNote: `Applied in the browser: ${kind} of ${timecode(range.startSec)} to ${timecode(range.endSec)}.`,
      });
      return updated;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(timelineKey, updated);
      void queryClient.invalidateQueries({ queryKey: notesKey });
      selectRange(null);
      setFailure(null);
    },
    onError: (error) => handleFailure("range", error),
  });

  /**
   * Attaches a source to an existing segment. What "drag a video onto the
   * timeline" means for a range that already has a segment in it
   * (ADR 000025/000026).
   */
  const attachFootage = useMutation({
    mutationFn: ({
      segmentId,
      change,
    }: {
      segmentId: Segment["id"];
      change: Parameters<typeof api.updateSegment>[3];
    }) => api.updateSegment(spaceId, projectId, segmentId, change),
    onSuccess: (updated) => {
      queryClient.setQueryData(timelineKey, updated);
      selectRange(null);
      setFailure(null);
      closeFootagePicker();
    },
    onError: (error) => handleFailure("footage", error),
  });

  /**
   * Writes back the words of one segment's brief (ADR 000026).
   *
   * Carries `expectedVersion` like every other write here, so a brief composed
   * against a chain somebody else has since changed is refused rather than
   * quietly landing on top of their edit (ADR 000027).
   */
  const saveBrief = useMutation({
    mutationFn: ({
      segmentId,
      change,
    }: {
      segmentId: Segment["id"];
      change: Parameters<typeof api.updateSegment>[3];
    }) => api.updateSegment(spaceId, projectId, segmentId, change),
    onSuccess: (updated) => {
      queryClient.setQueryData(timelineKey, updated);
      setEditingId(null);
      setFailure(null);
    },
    onError: (error) => handleFailure("brief", error),
  });

  /**
   * Takes one still off the segment being described (ADR 000071).
   *
   * A write of its own rather than part of Save, because no brief write can do
   * it: one that sends no stills keeps the ones the segment had, so that an
   * edit about words cannot throw away pictures (ADR 000070). It lands as it is
   * asked for, which is why Cancel below is about the words and never about
   * the frames.
   */
  const removeStill = useMutation({
    mutationFn: (what: { segmentId: Segment["id"]; index: number; expectedVersion: number }) =>
      api.removeSegmentStill(spaceId, projectId, what.segmentId, what.index, what.expectedVersion),
    onSuccess: (updated) => {
      queryClient.setQueryData(timelineKey, updated);
      setFailure(null);
    },
    onError: (error) => handleFailure("brief", error),
  });

  /**
   * Asks for a segment's picture to be made (ADR 000056).
   *
   * The worker claims the queued rendition, cuts the brief's sources with
   * ffmpeg and writes the file back, so the segment shows `queued` immediately
   * and `ready` once there is something to play. A render already made from
   * the same brief comes back as it is rather than being made twice.
   */
  const requestRender = useMutation({
    mutationFn: (what: { id: Segment["id"]; fidelity: Rendition["fidelity"] }) =>
      api.requestRender(spaceId, projectId, what.id, what.fidelity),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timelineKey });
      setFailure(null);
    },
    onError: (error) => handleFailure("range", error),
  });

  // The whole chain as one file, which is the last step of the job this
  // product exists to do (ADR 000061).
  const assemble = useMutation({
    mutationFn: () => api.requestAssembly(spaceId, projectId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timelineKey });
      setFailure(null);
    },
    onError: (error) => handleFailure("chain", error),
  });

  const createNote = useMutation({
    mutationFn: (draft: Parameters<typeof api.createNote>[2]) =>
      api.createNote(spaceId, projectId, draft),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notesKey });
      selectRange(null);
      setComment("");
      setFailure(null);
    },
    onError: (error) => handleFailure("comment", error),
  });

  const updateNote = useMutation({
    mutationFn: ({ id, status }: { id: Note["id"]; status: Note["status"] }) =>
      api.updateNote(spaceId, projectId, id, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notesKey });
      // Every other write here clears the line on success. Without it a
      // verdict refused on one note keeps answering for the next one.
      setFailure(null);
    },
    onError: (error, { id }) => handleFailure(`note:${id}`, error),
  });

  if (timeline.isPending) return <p role="status">Loading the timeline…</p>;
  if (timeline.error) return <p className="error">{describe(timeline.error)}</p>;

  const { segments, renditions, mediaUrls, posterUrls } = timeline.data;

  // A rendition naming no segment is the whole chain (ADR 000025). Whether the
  // newest one is still of this chain is the server's answer, not this page's:
  // re-render a segment and the assembly that exists is a version behind, which
  // it is impossible to see by looking at the assembly itself (ADR 000061).
  const {
    current: cut,
    superseded,
    failed: failedCut,
  } = assemblyOf(renditions, timeline.data.assemblyFingerprint);
  const cutUrl = cut ? mediaUrls[cut.id] : undefined;
  const durationSec = timeline.data.timeline.durationSec;
  // Sent with every write, so an edit made against a superseded chain is
  // refused rather than silently overwriting somebody else's (ADR 000027).
  const version = timeline.data.timeline.version;
  const allNotes = notes.data ?? [];
  const busy =
    replaceSegments.isPending ||
    createNote.isPending ||
    applyEdit.isPending ||
    attachFootage.isPending ||
    saveBrief.isPending ||
    removeStill.isPending ||
    requestRender.isPending;

  const touched = selection ? segmentsIn(segments, selection) : [];
  // Named apart from `touched[0]`, so the type checker knows a Render button
  // only ever renders next to a segment that actually exists.
  const onlyTouched = touched.length === 1 ? touched[0] : undefined;
  // The segment the brief editor is open on, which is only ever the one now
  // selected: moving the selection takes the editor away with it rather than
  // leaving one segment's words over another's.
  const editing = onlyTouched && onlyTouched.id === editingId ? onlyTouched : undefined;
  const draftBrief = editing
    ? withWords(editing.brief, {
        direction: draftDirection.trim(),
        narration: draftNarration.trim(),
      })
    : undefined;
  const alreadySaid = selection ? notesIn(allNotes, selection) : [];
  // What has been made of the one selected segment. The URL is short-lived and
  // signed, so nothing here holds a storage path (ADR 000057 does the same for
  // a share).
  const picture = onlyTouched ? pictureOf(renditions, onlyTouched) : undefined;
  const watchable = picture ? mediaUrls[picture.id] : undefined;
  // Each unmade state says which one it is. Queued, rendering, failed and never
  // asked for all look identical as an empty box, and only one of them is worth
  // waiting on. A made segment that will not sign is the last case: it is not
  // "not made", and saying so would send somebody to render it again.
  const saysAbout = onlyTouched
    ? madeState(onlyTouched, picture) ||
      (watchable ? "" : "Made, but it cannot be played just now. Reload to try again.")
    : "";
  const made = progress(segments);
  const plannedCount = segments.filter((segment) => segment.state === "planned").length;

  return (
    <section className="timeline">
      <header className="timeline-head">
        <h2 className="section-head">Timeline</h2>
        <p className="muted">
          Version {timeline.data.timeline.version} · {timecode(durationSec)} ·{" "}
          {segments.length === 1 ? "1 segment" : `${segments.length} segments`}
        </p>

        {/*
         * How much of the plan is made, which is what a project stage cannot
         * express: a long video is routinely part cut and part storyboard
         * (ADR 000025).
         */}
        {durationSec > 0 && (
          <p className="progress">
            <span
              className="progress-bar"
              style={{ ["--made" as string]: `${Math.round(made * 100)}%` }}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(made * 100)}
              aria-label="Made"
            />
            <span className="muted">
              {timecode(timeline.data.timeline.readyDurationSec)} of {timecode(durationSec)} made
              {plannedCount > 0 &&
                ` · ${plannedCount === 1 ? "1 segment" : `${plannedCount} segments`} still storyboard`}
            </span>
          </p>
        )}

        {/*
         * Getting the video out, which is the last step of the job and was
         * until now the one that happened in another tool (ADR 000061). The
         * assembly is a rendition of the chain rather than of a segment, so it
         * arrives with a signed URL like any other.
         *
         * A superseded assembly is offered as something to remake rather than
         * something to download. It plays, so nothing about it looks wrong, and
         * that is the point: the person handing it over is the one who cannot
         * tell it is a version behind the chain they are looking at.
         *
         * A failed one has to say so. The button on its own is the same button
         * that was there before anybody pressed it, so a silent failure reads
         * as nothing having happened and the obvious move is to press it again
         * and get the same failure (issue #390).
         */}
        {mayEdit && segments.length > 0 && (
          <p className="assembly row">
            {cut && cutUrl ? (
              <>
                <a className="button" href={cutUrl} download>
                  Download the cut
                </a>
                <span className="muted">
                  {timecode(cut.durationSec)}, assembled from {segments.length}{" "}
                  {segments.length === 1 ? "segment" : "segments"}
                </span>
              </>
            ) : cut && cut.status === "ready" ? (
              <span className="muted">
                Assembled, but it cannot be downloaded just now. Reload to try again.
              </span>
            ) : cut ? (
              <span className="muted">Assembling the whole chain…</span>
            ) : (
              <>
                <button
                  type="button"
                  disabled={assemble.isPending}
                  onClick={() => assemble.mutate()}
                >
                  {superseded || failedCut ? "Assemble the chain again" : "Assemble the whole cut"}
                </button>
                {failedCut ? (
                  <span className="error">{assemblyFailure(failedCut)}</span>
                ) : (
                  superseded && (
                    <span className="muted">
                      The chain has changed since it was last assembled, so what was made is a
                      version behind it.
                    </span>
                  )
                )}
                <RefusalLine refusal={failure} at="chain" inline />
              </>
            )}
          </p>
        )}
      </header>

      {/*
       * The chain played as one thing, assembled as it goes (issue #200). Above
       * the bar, because watching it is what the bar is for.
       */}
      <ChainPlayer
        spaceId={spaceId}
        projectId={projectId}
        segments={segments}
        renditions={renditions}
        mediaUrls={mediaUrls}
        posterUrls={posterUrls}
        durationSec={durationSec}
        playheadSec={playheadSec ?? 0}
        onPlayhead={setPlayheadSec}
      />

      <ScrubBar
        spaceId={spaceId}
        projectId={projectId}
        segments={segments}
        notes={allNotes}
        durationSec={durationSec}
        selection={selection}
        onSelect={selectRange}
        playheadSec={playheadSec}
        onScrub={setPlayheadSec}
        onHover={setHovered}
      />

      {/*
       * What is in the segment under the pointer, without leaving the chain
       * (ADR 000064). A segment holds different things at different times, so
       * this shows whichever it currently is: a picture that is made, the
       * stills standing in for one that is not, or the words nobody has shot.
       */}
      <SegmentPreview
        spaceId={spaceId}
        projectId={projectId}
        segment={hovered}
        picture={hovered ? pictureOf(renditions, hovered) : undefined}
        posterUrls={posterUrls}
      />

      <RefusalLine refusal={failure} at="panel" />

      {selection ? (
        <div className="range-menu">
          <p className="range-menu-head">
            <strong>
              {timecode(selection.startSec)} to {timecode(selection.endSec)}
            </strong>{" "}
            <span className="muted">
              {touched.length === 1 ? "1 segment" : `${touched.length} segments`}
            </span>
          </p>

          {/*
           * Watch, then say what is wrong with it. The first word of that loop
           * needs the cut on the same page as the bar it was selected on: a
           * note anchored to a range is written while watching that range
           * (ADR 000023), and until this was here the only way to see a
           * rendered segment was to share the project and open it as an
           * outsider (issue #148).
           */}
          {onlyTouched && (
            <div className="segment-picture">
              {watchable && picture && (
                <video
                  // Keyed by the rendition, so selecting a different segment
                  // replaces the element rather than swapping the source under
                  // a player that is already loaded and would keep the last one.
                  key={picture.id}
                  className="segment-player"
                  src={watchable}
                  poster={posterUrls[picture.id]}
                  controls
                  aria-label={`${onlyTouched.label || "This segment"}, rendered`}
                />
              )}
              {saysAbout && <p className="muted">{saysAbout}</p>}
              {/*
               * Notes this renderer could not follow, said next to the picture
               * that did not follow them. An instruction that quietly does
               * nothing is worse than one that is refused: without this, the
               * picture is not what somebody asked for and there is nothing to
               * look at that says why (ADR 000068).
               *
               * A rendition whose unappliedNotes is empty says nothing at all,
               * so the ordinary case stays quiet.
               */}
              {picture && picture.unappliedNotes && picture.unappliedNotes.length > 0 && (
                <p className="unapplied-notes">
                  This renderer did not follow:{" "}
                  {picture.unappliedNotes.map((key, i) => (
                    <span key={`${i}-${key}`}>
                      {i > 0 ? ", " : ""}
                      <code>{key}</code>
                    </span>
                  ))}
                  .
                </p>
              )}
            </div>
          )}

          <div className="range-actions">
            {mayEdit && (
              <>
                {/*
                 * Trim and cut go straight through: the edit is exact, so
                 * making somebody wait on a model to perform it would be
                 * theatre. They are still recorded as notes, because the
                 * history is what the agent reads on the next round
                 * (ADR 000023).
                 */}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    applyEdit.mutate({
                      kind: "trim",
                      range: selection,
                      segments: applyTrim(segments, selection),
                      expectedVersion: version,
                    })
                  }
                >
                  Trim
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    applyEdit.mutate({
                      kind: "cut",
                      range: selection,
                      segments: applyCut(segments, selection),
                      expectedVersion: version,
                    })
                  }
                >
                  Cut away
                </button>
                {/*
                 * Holding a place in the chain for something not yet shot or
                 * written. Direction and a length is a complete brief, which is
                 * what makes the storyboard a state rather than a stage
                 * (ADR 000026).
                 */}
                <button
                  type="button"
                  disabled={busy || touched.length > 0}
                  title={
                    touched.length > 0
                      ? "This range already has segments in it"
                      : "Hold this range for something not made yet"
                  }
                  onClick={() => {
                    const draft = plannedSegment(
                      selection.startSec,
                      selection.endSec,
                      comment.trim() || "To be planned.",
                    );
                    replaceSegments.mutate({
                      segments: [...segments.map(toDraft), draft],
                      expectedVersion: version,
                      where: "range",
                    });
                  }}
                >
                  Plan this range
                </button>
                {/*
                 * What "drag a video onto the timeline" means underneath: pick
                 * the asset and the part of it wanted, rather than a literal
                 * drag across pages (ADR 000025/000026).
                 */}
                <button
                  type="button"
                  disabled={busy || touched.length > 1}
                  title={
                    touched.length > 1 ? "Select a single segment, or an empty range" : undefined
                  }
                  onClick={() => setFootagePicker((open) => !open)}
                >
                  Use footage…
                </button>
                {onlyTouched && (
                  <>
                    {/*
                     * Saying what a segment is, which until now only an agent
                     * over MCP could do. A brief is direction and narration
                     * (ADR 000026), and writing a sentence is not agentic work:
                     * the core platform is complete on its own (ADR 000058).
                     */}
                    <button
                      type="button"
                      disabled={busy}
                      title="Say what happens in this segment, and what is spoken over it"
                      onClick={() => (editing ? setEditingId(null) : openBriefEditor(onlyTouched))}
                    >
                      Describe…
                    </button>
                    {/*
                     * Stills held for the shot's length with the narration
                     * spoken over them (ADR 000065). It answers "does this
                     * land?" before there is any footage, which is the whole
                     * point of asking it first.
                     */}
                    <button
                      type="button"
                      disabled={busy}
                      title="Hold this segment's stills for its length, with the narration spoken over"
                      onClick={() =>
                        requestRender.mutate({ id: onlyTouched.id, fidelity: "animatic" })
                      }
                    >
                      Animatic
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      title="Record this segment as queued for rendering"
                      onClick={() =>
                        requestRender.mutate({ id: onlyTouched.id, fidelity: "rough" })
                      }
                    >
                      Render
                    </button>
                  </>
                )}
              </>
            )}
            <button
              type="button"
              className="ghost"
              onClick={() => {
                selectRange(null);
                closeFootagePicker();
              }}
            >
              Cancel
            </button>
            <RefusalLine refusal={failure} at="range" />
          </div>

          {/*
           * The brief in words. Narration in particular was shown, played and
           * offered an Animatic button long before there was anywhere to type
           * it, which reads as a broken feature rather than an absent one
           * (issue #222).
           */}
          {mayEdit && editing && draftBrief && (
            <form
              className="brief-editor"
              onSubmit={(event) => {
                event.preventDefault();
                if (saysNothing(draftBrief)) return;
                saveBrief.mutate({
                  segmentId: editing.id,
                  change: {
                    expectedVersion: version,
                    label: draftLabel.trim(),
                    brief: draftBrief,
                  },
                });
              }}
            >
              <label htmlFor="brief-label">Name</label>
              <input
                id="brief-label"
                type="text"
                maxLength={300}
                value={draftLabel}
                placeholder="Cold open"
                onChange={(event) => setDraftLabel(event.target.value)}
              />

              <label htmlFor="brief-direction">Direction</label>
              <textarea
                id="brief-direction"
                rows={3}
                value={draftDirection}
                placeholder="Wide shot of the harbour at dawn, holding until the first boat leaves."
                onChange={(event) => setDraftDirection(event.target.value)}
              />

              <label htmlFor="brief-narration">Narration</label>
              <textarea
                id="brief-narration"
                rows={3}
                value={draftNarration}
                placeholder="Every morning, before the town is awake, the fleet is already out."
                onChange={(event) => setDraftNarration(event.target.value)}
              />

              {/*
               * The frames standing in for a picture that has not been made,
               * which an animatic holds in this order (ADR 000065). Shown here
               * because this is where somebody is already deciding what the
               * segment is, and until now they were carried through every edit
               * without ever being visible or removable (ADR 000071).
               */}
              {editing.brief.stills.length > 0 && (
                <>
                  <span className="brief-editor-heading">Stills</span>
                  <ul className="brief-stills">
                    {editing.brief.stills.map((still, index) => (
                      <li key={`${index}:${still}`}>
                        <img
                          src={api.segmentStillUrl(spaceId, projectId, editing.id, index)}
                          alt=""
                          loading="lazy"
                        />
                        <button
                          type="button"
                          className="ghost"
                          disabled={busy}
                          // Numbered, because "Remove" three times over is three
                          // buttons a screen reader cannot tell apart, and which
                          // frame is going is the whole decision.
                          aria-label={`Remove still ${index + 1}`}
                          title="Take this frame off the segment"
                          onClick={() =>
                            removeStill.mutate({
                              segmentId: editing.id,
                              index,
                              expectedVersion: version,
                            })
                          }
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/*
               * The one thing the API refuses outright, said before the write
               * rather than after it: a segment with no direction, no narration
               * and no footage is a place in the chain nothing says how to fill.
               */}
              {saysNothing(draftBrief) && (
                <p className="muted">
                  A segment needs direction, narration or footage. Write one of them, or cut the
                  segment away.
                </p>
              )}

              <div className="brief-editor-actions">
                <button type="submit" disabled={busy || saysNothing(draftBrief)}>
                  Save
                </button>
                <button type="button" className="ghost" onClick={() => setEditingId(null)}>
                  Cancel
                </button>
              </div>

              <RefusalLine refusal={failure} at="brief" />
            </form>
          )}

          {mayEdit && footagePicker && (
            <div className="footage-picker">
              {assets.isPending ? (
                <p className="muted">Loading the corpus…</p>
              ) : footage.length === 0 ? (
                <p className="muted">Nothing ready to use as footage yet.</p>
              ) : (
                <>
                  <label htmlFor="footage-asset">Asset</label>
                  <select
                    id="footage-asset"
                    value={pickedAssetId}
                    onChange={(event) => {
                      const id = event.target.value;
                      setPickedAssetId(id);
                      const asset = footage.find((candidate) => candidate.id === id);
                      setRangeStart(0);
                      setRangeEnd(asset?.durationSec ?? 0);
                    }}
                  >
                    <option value="">Choose an asset…</option>
                    {footage.map((asset) => (
                      <option key={asset.id} value={asset.id}>
                        {asset.name} ({timecode(asset.durationSec)})
                      </option>
                    ))}
                  </select>

                  {pickedAsset && (
                    <div className="footage-range">
                      <label htmlFor="footage-range-start">From</label>
                      <input
                        id="footage-range-start"
                        type="number"
                        min={0}
                        max={pickedAsset.durationSec}
                        step={0.1}
                        value={rangeStart}
                        onChange={(event) => setRangeStart(Number(event.target.value))}
                      />
                      <label htmlFor="footage-range-end">To</label>
                      <input
                        id="footage-range-end"
                        type="number"
                        min={0}
                        max={pickedAsset.durationSec}
                        step={0.1}
                        value={rangeEnd}
                        onChange={(event) => setRangeEnd(Number(event.target.value))}
                      />
                    </div>
                  )}

                  <button
                    type="button"
                    disabled={
                      busy ||
                      !pickedAsset ||
                      !(
                        rangeStart >= 0 &&
                        rangeEnd > rangeStart &&
                        rangeEnd <= pickedAsset.durationSec
                      )
                    }
                    onClick={() => {
                      if (!pickedAsset) return;
                      const source: BriefSource = {
                        assetId: pickedAsset.id,
                        range: { startSec: rangeStart, endSec: rangeEnd },
                      };

                      if (touched.length === 1) {
                        const target = touched[0];
                        if (!target) return;
                        attachFootage.mutate({
                          segmentId: target.id,
                          change: {
                            expectedVersion: version,
                            brief: withSource(target.brief, source),
                          },
                        });
                        return;
                      }

                      const draft: DraftSegment = {
                        track: 0,
                        startSec: selection.startSec,
                        endSec: selection.endSec,
                        brief: withSource(
                          {
                            kind: "generated",
                            direction: "",
                            narration: "",
                            sources: [],
                            stills: [],
                          },
                          source,
                        ),
                      };
                      replaceSegments.mutate({
                        segments: [...segments.map(toDraft), draft],
                        expectedVersion: version,
                        where: "footage",
                      });
                    }}
                  >
                    Attach
                  </button>
                </>
              )}

              <RefusalLine refusal={failure} at="footage" />
            </div>
          )}

          <form
            className="range-comment"
            onSubmit={(event) => {
              event.preventDefault();
              const body = comment.trim();
              if (!body) return;
              createNote.mutate({ track: 0, range: selection, kind: "comment", body });
            }}
          >
            <label htmlFor="range-comment">Or say what is wrong with it</label>
            <textarea
              id="range-comment"
              value={comment}
              rows={2}
              placeholder="Redo this part; the pacing drags and the narration lands late."
              onChange={(event) => setComment(event.target.value)}
            />
            <button type="submit" disabled={busy || comment.trim() === ""}>
              Leave a note
            </button>
            <RefusalLine refusal={failure} at="comment" />
          </form>

          {alreadySaid.length > 0 && (
            <p className="muted range-existing">
              {alreadySaid.length === 1
                ? "1 note already covers part of this range."
                : `${alreadySaid.length} notes already cover part of this range.`}
            </p>
          )}
        </div>
      ) : (
        <p className="muted hint">
          Drag across the bar to select a range, then trim it, cut it, or say what is wrong with it.
        </p>
      )}

      <h3 className="section-head">Notes</h3>
      {allNotes.length === 0 ? (
        <p className="muted">No notes on this cut.</p>
      ) : (
        <ul className="notes">
          {allNotes.map((note) => (
            <li key={note.id} className={`note is-${note.status}`}>
              <button
                type="button"
                className="note-range"
                onClick={() => {
                  selectRange(note.range);
                  setPlayheadSec(note.range.startSec);
                }}
              >
                {timecode(note.range.startSec)} to {timecode(note.range.endSec)}
              </button>
              <span className={`note-kind is-${note.kind}`}>{note.kind}</span>
              <p>
                {note.body || (
                  <span className="muted">No comment; the range is the whole of it.</span>
                )}
              </p>
              {note.resolutionNote && <p className="muted">{note.resolutionNote}</p>}
              {mayEdit && note.status === "addressed" && (
                <div className="note-verdict">
                  {/*
                   * The agent says it acted; a person says whether it worked.
                   * Keeping those two separate is what makes the history worth
                   * reading back (ADR 000023). Editor-only on the API, same as
                   * the write it is judging.
                   */}
                  <button
                    type="button"
                    onClick={() => updateNote.mutate({ id: note.id, status: "accepted" })}
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => updateNote.mutate({ id: note.id, status: "open" })}
                  >
                    Still wrong
                  </button>
                </div>
              )}
              {mayEdit && note.status === "open" && (
                <button
                  type="button"
                  className="ghost"
                  onClick={() => updateNote.mutate({ id: note.id, status: "dismissed" })}
                >
                  Dismiss
                </button>
              )}
              <RefusalLine refusal={failure} at={`note:${note.id}`} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * What is in one segment, shown while the pointer is over it (ADR 000064).
 *
 * The order is what is most true about the segment right now: a picture that
 * has actually been made beats the stills standing in for one, and the stills
 * beat the words describing what to shoot. A segment with none of those says so
 * rather than showing an empty box, because "nothing here yet" and "something
 * failed to load" look identical otherwise.
 */
function SegmentPreview({
  spaceId,
  projectId,
  segment,
  picture,
  posterUrls,
}: {
  spaceId: Space["id"];
  projectId: Project["id"];
  segment: Segment | null;
  picture: Rendition | undefined;
  posterUrls: Record<string, string>;
}) {
  // Kept in the layout when there is nothing to show, so the chain does not
  // jump under the pointer as it crosses a gap between two segments.
  if (!segment) return <div className="segment-preview is-empty" aria-hidden="true" />;

  const poster = picture ? posterUrls[picture.id] : undefined;
  // Each still asked of the API by its place in the brief rather than fetched
  // from the path the brief holds (ADR 000070).
  const stills = segment.brief.stills
    .slice(0, 4)
    .map((_, index) => api.segmentStillUrl(spaceId, projectId, segment.id, index));
  const words = segment.brief.direction || segment.brief.narration || segment.label || "";

  return (
    <div className="segment-preview">
      <div className="segment-preview-shot">
        {poster ? (
          <img src={poster} alt="" loading="lazy" />
        ) : stills.length > 0 ? (
          stills.map((still) => <img key={still} src={still} alt="" loading="lazy" />)
        ) : (
          !words && <span className="muted">Nothing in this segment yet</span>
        )}
      </div>

      <div className="segment-preview-words">
        <p className="segment-preview-what">
          {timecode(segment.startSec)}–{timecode(segment.endSec)}
          {segment.label ? ` · ${segment.label}` : ""}
        </p>
        {words && <p>{words}</p>}
        {segment.brief.narration && segment.brief.narration !== words && (
          <p className="muted">&ldquo;{segment.brief.narration}&rdquo;</p>
        )}
      </div>
    </div>
  );
}

/**
 * What to say about a segment nobody can watch yet, or can watch but should not
 * trust. Empty when the picture is current, because then the picture says it.
 *
 * The state is the server's, derived from the rendition the segment points at
 * (ADR 000025), so this reads it rather than working it out again from the
 * rendition's status.
 */
function madeState(segment: Segment, picture: Rendition | undefined): string {
  switch (segment.state) {
    case "queued":
      return "Queued. The worker picks this up on its next pass.";
    case "rendering":
      return "Being made now.";
    case "failed":
      return picture?.failureReason
        ? `This one did not come out: ${picture.failureReason}`
        : "This one did not come out. Ask for it again.";
    case "stale":
      return "Made from an earlier brief, so this is not what the segment now says. Render it again.";
    case "ready":
      return "";
    default:
      return "Not made yet.";
  }
}

/**
 * What to say about an assembly that did not come out.
 *
 * The reason is the renderer's own, written on the rendition when it gave up
 * (ADR 000056), and it is the only account of the failure that ever reaches
 * anybody: the request itself succeeded minutes earlier, so nothing the
 * mutation can catch says a word about this.
 */
function assemblyFailure(failed: Rendition): string {
  return failed.failureReason
    ? `The last assembly did not come out: ${failed.failureReason}`
    : "The last assembly did not come out. Ask for it again.";
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.userMessage;
  return error instanceof Error ? error.message : "Something went wrong.";
}
